import type {
  FC,
  HTMLAttributes,
  HTMLInputTypeAttribute,
  ReactNode,
} from "react";
import { useFormikContext } from "formik";
import { FormFieldWrapper } from "./FormFieldWrapper";
import {
  ScrollSafeDropdown,
  useScrollSafeDropdown,
} from "./ScrollSafeDropdown";
interface BaseFormFieldProps {
  name: string;
  label: string;
  id: string;
  helperText?: ReactNode;
  isRequired?: boolean;
}

interface TextFormFieldProps extends BaseFormFieldProps {
  type?: HTMLInputTypeAttribute;
  inputMode?: HTMLAttributes<HTMLInputElement>["inputMode"];
}

interface TextareaFormFieldProps extends BaseFormFieldProps {
  className?: string;
  rows?: number;
}

interface SelectFormFieldOption {
  value: string;
  label: ReactNode;
  disabled?: boolean;
}

interface SelectFormFieldProps extends BaseFormFieldProps {
  placeholder?: string;
  options: SelectFormFieldOption[];
}

const SelectFormFieldControl: FC<{
  field: {
    name: string;
    value: string;
    onChange: (event: { target: { name: string; value: string } }) => void;
    onBlur: (event: { target: { name: string; value: string } }) => void;
  };
  showError?: boolean;
  describedBy?: string;
  id: string;
  placeholder: string;
  options: SelectFormFieldOption[];
}> = ({ field, showError, describedBy, id, placeholder, options }) => {
  const { setFieldValue, setFieldTouched } = useFormikContext();
  const { open, setOpen, toggle } = useScrollSafeDropdown();
  const selectedOption = options.find((option) => option.value === field.value);
  const displayLabel = selectedOption?.label ?? placeholder;
  const hasValue = Boolean(field.value);

  const selectOption = (value: string) => {
    setOpen(false);
    void setFieldValue(field.name, value, true).then(() => {
      setFieldTouched(field.name, true, false);
    });
  };

  return (
    <ScrollSafeDropdown
      id={id}
      describedBy={describedBy}
      invalid={showError}
      open={open}
      onOpenChange={setOpen}
      trigger={
        <button
          type="button"
          className={`form-select text-start${invalidClass(showError)}`}
          onClick={toggle}
        >
          <span className={hasValue ? "" : "text-muted"}>{displayLabel}</span>
        </button>
      }
    >
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="option"
          aria-selected={field.value === option.value}
          disabled={option.disabled}
          className={`blanket-dropdown-menu__option${
            field.value === option.value ? " active" : ""
          }`}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => {
            if (!option.disabled) {
              selectOption(option.value);
            }
          }}
        >
          {option.label}
        </button>
      ))}
    </ScrollSafeDropdown>
  );
};
const invalidClass = (showError?: boolean) => (showError ? " is-invalid" : "");

export const TextFormField: FC<TextFormFieldProps> = ({
  name,
  label,
  id,
  helperText,
  isRequired,
  type = "text",
  inputMode,
}) => (
  <FormFieldWrapper
    name={name}
    label={label}
    id={id}
    helperText={helperText}
    isRequired={isRequired}
  >
    {({ field, showError, describedBy }) => (
      <input
        {...field}
        id={id}
        type={type}
        inputMode={inputMode}
        aria-describedby={describedBy}
        className={`form-control${invalidClass(showError)}`}
      />
    )}
  </FormFieldWrapper>
);

export const TextareaFormField: FC<TextareaFormFieldProps> = ({
  name,
  label,
  id,
  helperText,
  isRequired,
  className = "",
  rows = 5,
}) => (
  <FormFieldWrapper
    name={name}
    label={label}
    id={id}
    helperText={helperText}
    isRequired={isRequired}
  >
    {({ field, showError, describedBy }) => (
      <textarea
        {...field}
        id={id}
        rows={rows}
        aria-describedby={describedBy}
        className={`form-control${invalidClass(showError)} ${className}`.trim()}
      />
    )}
  </FormFieldWrapper>
);

export const SelectFormField: FC<SelectFormFieldProps> = ({
  name,
  label,
  id,
  helperText,
  isRequired,
  placeholder = "Vælg mulighed",
  options,
}) => (
  <FormFieldWrapper
    name={name}
    label={label}
    id={id}
    helperText={helperText}
    isRequired={isRequired}
  >
    {({ field, showError, describedBy }) => (
      <SelectFormFieldControl
        field={field}
        showError={showError}
        describedBy={describedBy}
        id={id}
        placeholder={placeholder}
        options={options}
      />
    )}
  </FormFieldWrapper>
);

export const YesNoFormField: FC<Omit<SelectFormFieldProps, "options">> = (
  props,
) => (
  <SelectFormField
    {...props}
    options={[
      { value: "Ja", label: "Ja" },
      { value: "Nej", label: "Nej" },
    ]}
  />
);
